import React from "react"

// Course color palette
const courseColors = [
  { bg: "bg-violet-100", text: "text-violet-700", dot: "bg-violet-500", accent: "#7c3aed" },
  { bg: "bg-cyan-100", text: "text-cyan-700", dot: "bg-cyan-500", accent: "#0891b2" },
  { bg: "bg-fuchsia-100", text: "text-fuchsia-700", dot: "bg-fuchsia-500", accent: "#c026d3" },
  { bg: "bg-orange-100", text: "text-orange-700", dot: "bg-orange-500", accent: "#ea580c" },
  { bg: "bg-emerald-100", text: "text-emerald-700", dot: "bg-emerald-500", accent: "#059669" },
  { bg: "bg-rose-100", text: "text-rose-700", dot: "bg-rose-500", accent: "#e11d48" },
  { bg: "bg-sky-100", text: "text-sky-700", dot: "bg-sky-500", accent: "#0284c7" },
  { bg: "bg-amber-100", text: "text-amber-700", dot: "bg-amber-500", accent: "#d97706" },
]

const CourseSidebar = ({
  courses,
  terms,
  selectedTerm,
  currentTermId,
  selectedCourse,
  onSelectTerm,
  onSelectCourse,
  courseAssignmentCounts,
  mobileOpen,
  onMobileOpenChange,
}) => {
  const getCourseColor = (index) => courseColors[index % courseColors.length]

  const visibleCourses = courses.filter(c => !c.hidden)

  const closeMobile = () => onMobileOpenChange(false)

  // Sidebar content
  const SidebarContent = ({ isMobile = false }) => (
    <div className={`flex flex-col h-full ${isMobile ? "" : "sticky top-6"}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-lg font-semibold text-[var(--ink)]">Courses</h2>
        {isMobile && (
          <button
            onClick={closeMobile}
            className="p-2 hover:bg-[var(--cream)] rounded-lg transition-colors"
          >
            <svg className="w-5 h-5 text-[var(--ink-muted)]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {/* Term Selector */}
      <div className="mb-4">
        <div className="relative">
          <select
            value={selectedTerm ?? ""}
            onChange={(e) => onSelectTerm(e.target.value === "" ? null : parseInt(e.target.value, 10))}
            className="w-full appearance-none pl-3 pr-10 py-2.5 bg-[var(--cream)] border-0 rounded-xl text-sm font-medium text-[var(--ink)] hover:bg-[var(--paper-warm)] focus:outline-none focus:ring-2 focus:ring-[var(--gold)] cursor-pointer transition-colors"
          >
            <option value="">All Semesters</option>
            {terms.map((term) => (
              <option key={term.id} value={term.id}>
                {term.name}{term.id === currentTermId ? " (Current)" : ""}
              </option>
            ))}
          </select>
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
            <svg className="h-4 w-4 text-[var(--ink-light)]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
      </div>

      {/* Course List */}
      <div className="flex-1 overflow-y-auto -mx-2 px-2">
        <nav className="space-y-1">
          {/* All Courses Option */}
          <button
            onClick={() => {
              onSelectCourse(null)
              if (isMobile) closeMobile()
            }}
            className={`course-tab w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-all ${
              selectedCourse === null
                ? "active bg-[var(--ink)] text-white"
                : "text-[var(--ink-muted)] hover:bg-[var(--cream)] hover:text-[var(--ink)]"
            }`}
          >
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
              selectedCourse === null ? "bg-white/20" : "bg-[var(--cream)]"
            }`}>
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <span className="font-medium block truncate">All Courses</span>
            </div>
          </button>

          {/* Divider */}
          {visibleCourses.length > 0 && (
            <div className="section-divider my-3" />
          )}

          {/* Course Items */}
          {visibleCourses.map((course, index) => {
            const color = getCourseColor(index)
            const counts = courseAssignmentCounts[course.id] || { total: 0, overdue: 0 }
            const isSelected = selectedCourse === course.id
            const hasGrade = course.current_score != null || course.current_grade

            // Get grade color based on score
            const getGradeColor = (score) => {
              if (score == null) return "text-[var(--ink-light)]"
              if (score >= 90) return "text-emerald-600"
              if (score >= 80) return "text-blue-600"
              if (score >= 70) return "text-amber-600"
              if (score >= 60) return "text-orange-600"
              return "text-red-600"
            }

            return (
              <button
                key={course.id}
                onClick={() => {
                  onSelectCourse(course.id)
                  if (isMobile) closeMobile()
                }}
                className={`course-tab w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-all group ${
                  isSelected
                    ? `active ${color.bg} ${color.text}`
                    : "text-[var(--ink-muted)] hover:bg-[var(--cream)] hover:text-[var(--ink)]"
                }`}
                style={isSelected ? { '--accent-color': color.accent } : undefined}
              >
                {/* Course Icon */}
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  isSelected ? "bg-white/50" : color.bg
                }`}>
                  <div className={`w-2.5 h-2.5 rounded-full ${color.dot}`} />
                </div>

                {/* Course Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium truncate text-sm">
                      {course.course_code || course.name}
                    </span>
                    {/* Grade Badge */}
                    {hasGrade && (
                      <span className={`text-xs font-semibold flex-shrink-0 ${
                        isSelected ? "opacity-90" : getGradeColor(course.current_score)
                      }`}>
                        {course.current_grade || `${Math.round(course.current_score)}%`}
                      </span>
                    )}
                  </div>
                  {course.course_code && course.name !== course.course_code && (
                    <span className={`text-xs truncate block ${
                      isSelected ? "opacity-70" : "text-[var(--ink-light)]"
                    }`}>
                      {course.name.length > 30 ? course.name.substring(0, 30) + "..." : course.name}
                    </span>
                  )}
                </div>

                {/* Count Badge */}
                {counts.total > 0 && (
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {counts.overdue > 0 && (
                      <span className="px-1.5 py-0.5 bg-red-100 text-red-700 rounded text-xs font-semibold">
                        {counts.overdue}
                      </span>
                    )}
                    <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                      isSelected
                        ? "bg-white/50"
                        : "bg-[var(--paper)] text-[var(--ink-light)]"
                    }`}>
                      {counts.total}
                    </span>
                  </div>
                )}
              </button>
            )
          })}

          {visibleCourses.length === 0 && (
            <div className="text-center py-8">
              <p className="text-sm text-[var(--ink-light)]">No courses in this term</p>
            </div>
          )}
        </nav>
      </div>
    </div>
  )

  return (
    <>
      {/* Desktop Sidebar */}
      <aside className="hidden lg:block w-64 flex-shrink-0">
        <SidebarContent />
      </aside>

      {/* Mobile Drawer */}
      {mobileOpen && (
        <>
          {/* Backdrop */}
          <div
            className="lg:hidden fixed inset-0 z-40 bg-black/30 backdrop-blur-sm"
            onClick={closeMobile}
          />

          {/* Drawer */}
          <div className="lg:hidden fixed inset-y-0 left-0 z-50 w-80 max-w-[85vw] bg-[var(--paper)] shadow-2xl p-6 overflow-y-auto">
            <SidebarContent isMobile />
          </div>
        </>
      )}
    </>
  )
}

export default CourseSidebar
